import React, { useState, useEffect } from 'react';

interface SocketMetrics {
  megabytesPerSecond: string;
  messagesPerSecond: string;
  uptime: string;
  topEvents: Array<{
    event: string;
    mbps: string;
    avgSize: string;
  }>;
}

interface NPCMetrics {
  total: number;
  alive: number;
  dead: number;
  spatialGridChunks: number;
  avgNPCsPerChunk: string;
}

interface ShipMetrics {
  total: number;
  playerShips: number;
  npcShips: number;
  activePlayers: number;
}

interface PlayerMetrics {
  connected: number;
}

interface P2PTradingMetrics {
  activeTrades: number;
  pendingRequests: number;
  completedTradesToday: number;
  volumeTodayBerries: number;
  volumeTodayItems: number;
}

interface AllMetrics {
  socket: SocketMetrics;
  npcs: NPCMetrics;
  ships: ShipMetrics;
  players: PlayerMetrics;
  p2pTrading?: P2PTradingMetrics;
}

interface MarketplaceMetrics {
  totalActiveOrders: number;
  totalBuyOrders: number;
  totalSellOrders: number;
  totalTransactionsToday: number;
  totalVolumeToday: number;
  totalFeesCollectedToday: number;
  totalTransactionsAllTime: number;
  topTradedItems: Array<{ itemId: string; volume: number; transactions: number }>;
  recentTransactions: Array<{
    id: string;
    itemId: string;
    quantity: number;
    pricePerUnit: number;
    totalValue: number;
    fee: number;
    buyerId: string;
    sellerId: string;
    timestamp: string;
  }>;
}

interface EconomyMetrics {
  marketplace: MarketplaceMetrics;
  p2pTrading: P2PTradingMetrics & {
    recentTrades: Array<{
      id: string;
      player1Name: string;
      player2Name: string;
      player1Berries: number;
      player2Berries: number;
      timestamp: number;
    }>;
    topTraders: Array<{ characterId: string; tradeCount: number }>;
  };
}

interface AdminMetricsPanelProps {
  isVisible: boolean;
  onClose: () => void;
}

export const AdminMetricsPanel: React.FC<AdminMetricsPanelProps> = ({ isVisible, onClose }) => {
  const [metrics, setMetrics] = useState<AllMetrics | null>(null);
  const [economyMetrics, setEconomyMetrics] = useState<EconomyMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [expandedSection, setExpandedSection] = useState<string | null>('socket');

  useEffect(() => {
    if (!isVisible) return;

    const fetchMetrics = async () => {
      try {
        setLoading(true);
        const [allResponse, economyResponse] = await Promise.all([
          fetch('/api/metrics/all'),
          fetch('/api/metrics/economy'),
        ]);
        if (!allResponse.ok) throw new Error('Failed to fetch metrics');
        const data = await allResponse.json();
        setMetrics(data);
        
        if (economyResponse.ok) {
          const economyData = await economyResponse.json();
          setEconomyMetrics(economyData);
        }
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();

    if (autoRefresh) {
      const interval = setInterval(fetchMetrics, 2000);
      return () => clearInterval(interval);
    }
  }, [isVisible, autoRefresh]);

  if (!isVisible) return null;

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const getHealthColor = (mbps: number) => {
    if (mbps > 50) return 'text-red-400';
    if (mbps > 30) return 'text-yellow-400';
    return 'text-green-400';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" style={{ fontFamily: 'var(--font-mono)' }}>
      <div className="bg-gray-900 border-2 border-blue-600 w-full max-w-2xl max-h-96 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800 border-b border-blue-600 p-4 flex justify-between items-center">
          <h2 className="text-lg font-bold text-blue-400">SERVER PERFORMANCE METRICS</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-3 py-1 border border-gray-600 text-xs font-bold ${
                autoRefresh ? 'bg-green-900 text-green-300' : 'bg-gray-800 text-gray-400'
              }`}
            >
              {autoRefresh ? 'AUTO REFRESH' : 'MANUAL'}
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1 border border-gray-600 bg-gray-800 text-gray-400 hover:bg-gray-700 text-xs font-bold"
            >
              CLOSE
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          {error && <div className="text-red-400 text-sm">Error: {error}</div>}
          {loading && !metrics && <div className="text-yellow-400 text-sm">Loading metrics...</div>}

          {metrics && (
            <>
              {/* Socket Metrics */}
              <div className="border border-blue-600">
                <button
                  onClick={() => toggleSection('socket')}
                  className="w-full bg-blue-900 hover:bg-blue-800 p-3 text-left font-bold text-blue-300 flex justify-between items-center"
                >
                  <span>SOCKET BANDWIDTH</span>
                  <span className="text-xs">{expandedSection === 'socket' ? 'v' : '>'}</span>
                </button>
                {expandedSection === 'socket' && (
                  <div className="bg-gray-800 p-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Total Bandwidth:</span>
                      <span className={`font-bold ${getHealthColor(parseFloat(metrics.socket.megabytesPerSecond))}`}>
                        {metrics.socket.megabytesPerSecond} MB/s
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Messages/sec:</span>
                      <span className="text-yellow-300 font-bold">{metrics.socket.messagesPerSecond}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Uptime:</span>
                      <span className="text-gray-400">{metrics.socket.uptime}s</span>
                    </div>
                    <div className="border-t border-gray-700 pt-2 mt-2">
                      <div className="text-gray-400 font-bold mb-2">Top Events (by bandwidth):</div>
                      <div className="space-y-1">
                        {metrics.socket.topEvents.map((event, i) => (
                          <div key={i} className="flex justify-between text-xs text-gray-400">
                            <span>{i + 1}. {event.event}</span>
                            <span className="text-orange-300">{event.mbps} MB/s</span>
                            <span className="text-gray-500">({event.avgSize}B avg)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* NPC Metrics */}
              <div className="border border-green-600">
                <button
                  onClick={() => toggleSection('npcs')}
                  className="w-full bg-green-900 hover:bg-green-800 p-3 text-left font-bold text-green-300 flex justify-between items-center"
                >
                  <span>NPC MANAGER & SPATIAL GRID</span>
                  <span className="text-xs">{expandedSection === 'npcs' ? 'v' : '>'}</span>
                </button>
                {expandedSection === 'npcs' && (
                  <div className="bg-gray-800 p-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Total NPCs:</span>
                      <span className="text-green-300 font-bold">{metrics.npcs.total}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Alive:</span>
                      <span className="text-green-300 font-bold">{metrics.npcs.alive}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Dead:</span>
                      <span className="text-red-300 font-bold">{metrics.npcs.dead}</span>
                    </div>
                    <div className="border-t border-gray-700 pt-2 mt-2">
                      <div className="text-gray-400 font-bold mb-2">Spatial Grid Performance:</div>
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>Chunks:</span>
                        <span className="text-cyan-300">{metrics.npcs.spatialGridChunks}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>Avg NPCs/Chunk:</span>
                        <span className="text-cyan-300">{metrics.npcs.avgNPCsPerChunk}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Ship Metrics */}
              <div className="border border-purple-600">
                <button
                  onClick={() => toggleSection('ships')}
                  className="w-full bg-purple-900 hover:bg-purple-800 p-3 text-left font-bold text-purple-300 flex justify-between items-center"
                >
                  <span>SHIPS</span>
                  <span className="text-xs">{expandedSection === 'ships' ? 'v' : '>'}</span>
                </button>
                {expandedSection === 'ships' && (
                  <div className="bg-gray-800 p-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Total Ships:</span>
                      <span className="text-purple-300 font-bold">{metrics.ships.total}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Player Ships:</span>
                      <span className="text-blue-300 font-bold">{metrics.ships.playerShips}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">NPC Ships:</span>
                      <span className="text-orange-300 font-bold">{metrics.ships.npcShips}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Active Players:</span>
                      <span className="text-cyan-300 font-bold">{metrics.ships.activePlayers}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Player Metrics */}
              <div className="border border-cyan-600">
                <button
                  onClick={() => toggleSection('players')}
                  className="w-full bg-cyan-900 hover:bg-cyan-800 p-3 text-left font-bold text-cyan-300 flex justify-between items-center"
                >
                  <span>PLAYERS</span>
                  <span className="text-xs">{expandedSection === 'players' ? 'v' : '>'}</span>
                </button>
                {expandedSection === 'players' && (
                  <div className="bg-gray-800 p-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Connected Players:</span>
                      <span className="text-cyan-300 font-bold">{metrics.players.connected}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Marketplace Metrics */}
              <div className="border border-yellow-600">
                <button
                  onClick={() => toggleSection('marketplace')}
                  className="w-full bg-yellow-900 hover:bg-yellow-800 p-3 text-left font-bold text-yellow-300 flex justify-between items-center"
                >
                  <span>MARKETPLACE</span>
                  <span className="text-xs">{expandedSection === 'marketplace' ? 'v' : '>'}</span>
                </button>
                {expandedSection === 'marketplace' && economyMetrics && (
                  <div className="bg-gray-800 p-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Active Orders:</span>
                      <span className="text-yellow-300 font-bold">{economyMetrics.marketplace.totalActiveOrders}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 pl-2">
                      <span>Buy Orders:</span>
                      <span className="text-green-300">{economyMetrics.marketplace.totalBuyOrders}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 pl-2">
                      <span>Sell Orders:</span>
                      <span className="text-red-300">{economyMetrics.marketplace.totalSellOrders}</span>
                    </div>
                    <div className="border-t border-gray-700 pt-2 mt-2">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Transactions Today:</span>
                        <span className="text-yellow-300 font-bold">{economyMetrics.marketplace.totalTransactionsToday}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Volume Today:</span>
                        <span className="text-yellow-300 font-bold">{economyMetrics.marketplace.totalVolumeToday.toLocaleString()} items</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Fees Collected:</span>
                        <span className="text-green-300 font-bold">{economyMetrics.marketplace.totalFeesCollectedToday.toLocaleString()} 🫐</span>
                      </div>
                    </div>
                    <div className="border-t border-gray-700 pt-2 mt-2">
                      <div className="flex justify-between">
                        <span className="text-gray-300">All-Time Transactions:</span>
                        <span className="text-gray-400">{economyMetrics.marketplace.totalTransactionsAllTime.toLocaleString()}</span>
                      </div>
                    </div>
                    {economyMetrics.marketplace.topTradedItems.length > 0 && (
                      <div className="border-t border-gray-700 pt-2 mt-2">
                        <div className="text-gray-400 font-bold mb-2">Top Traded Items (7d):</div>
                        <div className="space-y-1">
                          {economyMetrics.marketplace.topTradedItems.slice(0, 5).map((item, i) => (
                            <div key={i} className="flex justify-between text-xs text-gray-400">
                              <span>{i + 1}. {item.itemId}</span>
                              <span className="text-orange-300">{item.volume.toLocaleString()} traded</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {economyMetrics.marketplace.recentTransactions.length > 0 && (
                      <div className="border-t border-gray-700 pt-2 mt-2">
                        <div className="text-gray-400 font-bold mb-2">Recent Transactions:</div>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {economyMetrics.marketplace.recentTransactions.slice(0, 5).map((tx, i) => (
                            <div key={i} className="text-xs text-gray-400 p-1 bg-gray-900 rounded">
                              <div className="flex justify-between">
                                <span>{tx.quantity}x {tx.itemId}</span>
                                <span className="text-yellow-300">{(tx.totalValue).toLocaleString()} 🫐</span>
                              </div>
                              <div className="flex justify-between text-gray-500">
                                <span>@ {tx.pricePerUnit.toLocaleString()} each</span>
                                <span className="text-green-400">Fee: {tx.fee.toLocaleString()}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* P2P Trading Metrics */}
              <div className="border border-pink-600">
                <button
                  onClick={() => toggleSection('p2p')}
                  className="w-full bg-pink-900 hover:bg-pink-800 p-3 text-left font-bold text-pink-300 flex justify-between items-center"
                >
                  <span>P2P TRADING</span>
                  <span className="text-xs">{expandedSection === 'p2p' ? 'v' : '>'}</span>
                </button>
                {expandedSection === 'p2p' && (
                  <div className="bg-gray-800 p-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Active Trades:</span>
                      <span className="text-pink-300 font-bold">{metrics.p2pTrading?.activeTrades || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Pending Requests:</span>
                      <span className="text-orange-300 font-bold">{metrics.p2pTrading?.pendingRequests || 0}</span>
                    </div>
                    <div className="border-t border-gray-700 pt-2 mt-2">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Completed Today:</span>
                        <span className="text-green-300 font-bold">{metrics.p2pTrading?.completedTradesToday || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Berries Traded:</span>
                        <span className="text-yellow-300 font-bold">{(metrics.p2pTrading?.volumeTodayBerries || 0).toLocaleString()} 🫐</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Items Traded:</span>
                        <span className="text-blue-300 font-bold">{(metrics.p2pTrading?.volumeTodayItems || 0).toLocaleString()}</span>
                      </div>
                    </div>
                    {economyMetrics && economyMetrics.p2pTrading?.topTraders?.length > 0 && (
                      <div className="border-t border-gray-700 pt-2 mt-2">
                        <div className="text-gray-400 font-bold mb-2">Top Traders (7d):</div>
                        <div className="space-y-1">
                          {economyMetrics.p2pTrading.topTraders.slice(0, 5).map((trader, i) => (
                            <div key={i} className="flex justify-between text-xs text-gray-400">
                              <span>{i + 1}. {trader.characterId.slice(0, 8)}...</span>
                              <span className="text-pink-300">{trader.tradeCount} trades</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {economyMetrics && economyMetrics.p2pTrading?.recentTrades?.length > 0 && (
                      <div className="border-t border-gray-700 pt-2 mt-2">
                        <div className="text-gray-400 font-bold mb-2">Recent Trades:</div>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {economyMetrics.p2pTrading.recentTrades.slice(0, 5).map((trade, i) => (
                            <div key={i} className="text-xs text-gray-400 p-1 bg-gray-900 rounded">
                              <div className="flex justify-between">
                                <span>{trade.player1Name} ↔ {trade.player2Name}</span>
                              </div>
                              <div className="flex justify-between text-gray-500">
                                <span>{trade.player1Berries.toLocaleString()} 🫐</span>
                                <span>{trade.player2Berries.toLocaleString()} 🫐</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-800 border-t border-blue-600 p-3 text-xs text-gray-500 flex justify-between items-center">
          <span>
            {autoRefresh && <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse" />}
            {autoRefresh ? 'Auto-refreshing every 2s' : 'Manual mode - click AUTO REFRESH to enable'}
          </span>
          <span>Last updated: {new Date().toLocaleTimeString()}</span>
        </div>
      </div>
    </div>
  );
};
